import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { authedFetch, authedUrl } from '../lib/supabase';
import * as tus from 'tus-js-client';
import { collectFromDataTransfer, fromFileList, emptyDirsToCreate, joinPath, runWithConcurrency } from '../lib/dropEntries';
import { Logo } from '../components/ui';
import {
  colors, type, font, radius, shadow, transition, btn, heading,
  input as inputStyle, select as selectStyle, card as cardStyle, label as labelStyle,
  dialog as dialogStyle, status as statusColors,
} from '../theme';

const IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp'];
const VIDEO_EXTENSIONS = ['mp4', 'mov', 'avi', 'mkv', 'webm'];
const AUDIO_EXTENSIONS = ['mp3', 'wav', 'flac', 'aac', 'ogg', 'm4a'];
const THUMB_EXTENSIONS = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'mp4', 'mov', 'avi', 'mkv', 'webm'];
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';
const WEB_URL = process.env.REACT_APP_WEB_URL || window.location.origin;
const TUS_THRESHOLD = 5 * 1024 * 1024; // 5MB
// Matches the desktop sync client. Dropping a folder can queue hundreds of
// files; starting them all at once would stall the browser and the tunnel.
const UPLOAD_CONCURRENCY = 3;

function formatBytes(bytes) {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(i > 0 ? 1 : 0)} ${sizes[i]}`;
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function getFileIcon(extension) {
  const ext = (extension || '').toLowerCase();
  if (VIDEO_EXTENSIONS.includes(ext)) return 'video';
  if (['mp3', 'wav', 'flac', 'aac', 'ogg', 'm4a'].includes(ext)) return 'audio';
  if (IMAGE_EXTENSIONS.includes(ext)) return 'image';
  if (['psd', 'ai', 'prproj', 'aep', 'drp', 'fcpx'].includes(ext)) return 'project';
  if (['pdf', 'doc', 'docx', 'txt', 'md'].includes(ext)) return 'doc';
  if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) return 'archive';
  return 'file';
}

function hasThumbnail(item) {
  return item.type === 'file' && THUMB_EXTENSIONS.includes((item.extension || '').toLowerCase());
}

// ─── Thumbnail component ───
function Thumbnail({ filePath, size = 80 }) {
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    // Reset so a Thumbnail instance reused across a different filePath (list
    // reorder / folder change) doesn't keep the previous file's failed/url and
    // render blank for a valid new file.
    setUrl(null);
    setFailed(false);
    authedUrl(`/api/nas/thumb?path=${encodeURIComponent(filePath)}`)
      .then(u => { if (!cancelled) setUrl(u); })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, [filePath]);

  if (failed || !url) return null;

  return (
    <img
      src={url}
      alt=""
      width={size}
      height={size}
      style={{ objectFit: 'cover', borderRadius: size > 40 ? '6px' : '3px', flexShrink: 0 }}
      onError={() => setFailed(true)}
    />
  );
}

export default function Drive() {
  const { user, signOut } = useAuth();
  const [nasStatus, setNasStatus] = useState(null);
  const [currentPath, setCurrentPath] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [viewMode, setViewMode] = useState('list');
  const [error, setError] = useState(null);

  // Upload state
  const [uploads, setUploads] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [dragOverFolder, setDragOverFolder] = useState(null);
  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);
  // Monotonic counter so two files with the same name dropped in the same
  // millisecond (common inside a folder tree) still get distinct row ids.
  const uploadSeq = useRef(0);

  // Ref mirror of currentPath so long-running upload callbacks can read the
  // latest value instead of the one captured when the upload started.
  const currentPathRef = useRef(currentPath);
  useEffect(() => { currentPathRef.current = currentPath; }, [currentPath]);

  // Track active TUS uploads so we can abort them on unmount
  const activeTusUploads = useRef(new Map());
  useEffect(() => {
    return () => {
      activeTusUploads.current.forEach(upload => upload.abort());
      activeTusUploads.current.clear();
    };
  }, []);

  // Context menu
  const [contextMenu, setContextMenu] = useState(null);

  // Rename
  const [renaming, setRenaming] = useState(null);
  const [renameValue, setRenameValue] = useState('');

  // New folder
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');

  // Preview URL (authenticated)
  const [previewUrl, setPreviewUrl] = useState(null);

  // Sidebar navigation
  const [activeView, setActiveView] = useState('files');

  // Share links state
  const [shareLinks, setShareLinks] = useState([]);
  const [shareLinksLoading, setShareLinksLoading] = useState(false);
  const [showCreateShare, setShowCreateShare] = useState(null);
  const [shareMode, setShareMode] = useState('upload');
  const [shareExpiry, setShareExpiry] = useState('');
  const [shareMaxUses, setShareMaxUses] = useState('');
  const [createdShareLink, setCreatedShareLink] = useState(null);
  const [copiedLink, setCopiedLink] = useState(null);

  // Trash state
  const [trashItems, setTrashItems] = useState([]);
  const [trashLoading, setTrashLoading] = useState(false);

  // ─── Multi-select state ───
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [lastClickedIndex, setLastClickedIndex] = useState(null);

  // ─── Move dialog state ───
  const [showMoveDialog, setShowMoveDialog] = useState(false);
  const [moveItemPaths, setMoveItemPaths] = useState([]);
  const [moveFolders, setMoveFolders] = useState([]);
  const [movePath, setMovePath] = useState('');
  const [moveLoading, setMoveLoading] = useState(false);

  // ─── Favorites state ───
  const [favorites, setFavorites] = useState(new Set());
  const [favoriteItems, setFavoriteItems] = useState([]);
  const [favoritesLoading, setFavoritesLoading] = useState(false);

  // ─── User role state ───
  const [userRole, setUserRole] = useState(null);

  // ─── Access management dialog state ───
  const [showAccessDialog, setShowAccessDialog] = useState(null);
  const [accessBlocked, setAccessBlocked] = useState({ member: false, viewer: false });
  const [accessLoading, setAccessLoading] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [blockedUsers, setBlockedUsers] = useState(new Set());

  // ─── Settings state ───
  const [storageInfo, setStorageInfo] = useState(null);

  // ─── API Keys state ───
  const [apiKeys, setApiKeys] = useState([]);
  const [apiKeysLoading, setApiKeysLoading] = useState(false);
  const [showCreateKey, setShowCreateKey] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [createdKey, setCreatedKey] = useState(null);

  // Health check
  const checkHealth = useCallback(async () => {
    setNasStatus(null);
    try {
      const data = await authedFetch('/api/nas/health');
      setNasStatus(data.connected === true);
    } catch {
      setNasStatus(false);
    }
  }, []);

  useEffect(() => { checkHealth(); }, [checkHealth]);

  // Fetch user role on mount
  useEffect(() => {
    authedFetch('/api/me')
      .then(data => setUserRole(data?.user?.profileRole || null))
      .catch(() => {});
  }, []);

  // Fetch listing
  const fetchListing = useCallback(async (dirPath) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ path: dirPath || '', sort: sortBy, order: sortOrder });
      const data = await authedFetch(`/api/nas/list?${params}`);
      setItems(data.items || []);
    } catch (err) {
      setError(err.message);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [sortBy, sortOrder]);

  useEffect(() => {
    if (activeView === 'files') fetchListing(currentPath);
  }, [currentPath, fetchListing, activeView]);

  // ─── Share Links API ───
  const fetchShareLinks = useCallback(async () => {
    setShareLinksLoading(true);
    try {
      const data = await authedFetch('/api/shares');
      setShareLinks(Array.isArray(data) ? data : []);
    } catch {
      setShareLinks([]);
    } finally {
      setShareLinksLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeView === 'shared') fetchShareLinks();
  }, [activeView, fetchShareLinks]);

  function openCreateShare(itemPath, itemType) {
    setShowCreateShare({ path: itemPath, type: itemType });
    setShareMode('upload');
    setShareExpiry('');
    setShareMaxUses('');
    setCreatedShareLink(null);
  }

  async function createShareLink() {
    try {
      const body = {
        target_path: showCreateShare.path,
        mode: shareMode,
      };
      if (shareExpiry) {
        const hoursUntil = Math.max(1, Math.round((new Date(shareExpiry) - Date.now()) / 3600000));
        body.expires_in_hours = hoursUntil;
      }
      if (shareMaxUses) body.max_uses = parseInt(shareMaxUses, 10);

      const data = await authedFetch('/api/shares', {
        method: 'POST',
        body: JSON.stringify(body),
      });
      setCreatedShareLink(`${WEB_URL}/drop/${data.token}`);
    } catch (err) {
      alert('Failed to create share link: ' + err.message);
    }
  }

  async function revokeShareLink(id) {
    if (!window.confirm('Revoke this share link?')) return;
    try {
      await authedFetch(`/api/shares/${id}`, { method: 'DELETE' });
      fetchShareLinks();
    } catch (err) {
      alert('Revoke failed: ' + err.message);
    }
  }

  function copyShareUrl(url) {
    // Only show "Copied!" if the write actually succeeded, and swallow the
    // rejection (denied permission / non-secure context) so it isn't unhandled.
    navigator.clipboard.writeText(url).then(() => {
      setCopiedLink(url);
      setTimeout(() => setCopiedLink(null), 2000);
    }).catch(() => {});
  }

  // ─── Access management API ───
  async function openAccessDialog(item) {
    setShowAccessDialog(item);
    setAccessLoading(true);
    try {
      const [restrictionsData, usersData] = await Promise.all([
        authedFetch(`/api/restrictions?folder_path=${encodeURIComponent(item.path)}`),
        authedFetch('/api/restrictions/admin/users'),
      ]);
      setAccessBlocked(restrictionsData.blocked || { member: false, viewer: false });
      setBlockedUsers(new Set(restrictionsData.blocked_users || []));
      setAllUsers(usersData || []);
    } catch {
      setAccessBlocked({ member: false, viewer: false });
      setBlockedUsers(new Set());
      setAllUsers([]);
    } finally {
      setAccessLoading(false);
    }
  }

  async function saveAccessRestrictions() {
    if (!showAccessDialog) return;
    setAccessLoading(true);
    try {
      await authedFetch('/api/restrictions', {
        method: 'PUT',
        body: JSON.stringify({
          folder_path: showAccessDialog.path,
          blocked: accessBlocked,
          blocked_users: [...blockedUsers],
        }),
      });
      setShowAccessDialog(null);
    } catch (err) {
      alert('Failed to save restrictions: ' + err.message);
    } finally {
      setAccessLoading(false);
    }
  }

  // ─── Trash API ───
  const fetchTrash = useCallback(async () => {
    setTrashLoading(true);
    try {
      const data = await authedFetch('/api/nas/trash');
      setTrashItems(data.items || []);
    } catch {
      setTrashItems([]);
    } finally {
      setTrashLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeView === 'trash') fetchTrash();
  }, [activeView, fetchTrash]);

  async function restoreTrashItem(trashName) {
    try {
      await authedFetch('/api/nas/trash/restore', {
        method: 'POST',
        body: JSON.stringify({ trashName }),
      });
      fetchTrash();
    } catch (err) {
      alert('Restore failed: ' + err.message);
    }
  }

  async function permanentDeleteTrashItem(trashName) {
    if (!window.confirm('Permanently delete this item? This cannot be undone.')) return;
    try {
      await authedFetch('/api/nas/trash/delete', {
        method: 'DELETE',
        body: JSON.stringify({ trashName }),
      });
      fetchTrash();
    } catch (err) {
      alert('Delete failed: ' + err.message);
    }
  }

  async function emptyTrash() {
    if (!window.confirm('Permanently delete all items in trash? This cannot be undone.')) return;
    try {
      await authedFetch('/api/nas/trash/empty', { method: 'DELETE' });
      fetchTrash();
    } catch (err) {
      alert('Empty trash failed: ' + err.message);
    }
  }

  // ─── Favorites API ───
  const fetchFavoritePaths = useCallback(async () => {
    try {
      const data = await authedFetch('/api/nas/favorites');
      setFavorites(new Set((data || []).map(f => f.file_path)));
    } catch {
      setFavorites(new Set());
    }
  }, []);

  useEffect(() => { fetchFavoritePaths(); }, [fetchFavoritePaths]);

  const fetchFavoriteItems = useCallback(async () => {
    setFavoritesLoading(true);
    try {
      const data = await authedFetch('/api/nas/favorites');
      setFavoriteItems(data || []);
      setFavorites(new Set((data || []).map(f => f.file_path)));
    } catch {
      setFavoriteItems([]);
    } finally {
      setFavoritesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeView === 'favorites') fetchFavoriteItems();
  }, [activeView, fetchFavoriteItems]);

  async function toggleFavorite(filePath) {
    const isFav = favorites.has(filePath);
    // Optimistic update
    setFavorites(prev => {
      const next = new Set(prev);
      if (isFav) next.delete(filePath); else next.add(filePath);
      return next;
    });
    try {
      if (isFav) {
        await authedFetch('/api/nas/favorites', { method: 'DELETE', body: JSON.stringify({ file_path: filePath }) });
      } else {
        await authedFetch('/api/nas/favorites', { method: 'POST', body: JSON.stringify({ file_path: filePath }) });
      }
    } catch {
      // Revert on error
      setFavorites(prev => {
        const next = new Set(prev);
        if (isFav) next.add(filePath); else next.delete(filePath);
        return next;
      });
    }
  }

  // ─── Storage API ───
  async function fetchStorage() {
    try {
      const data = await authedFetch('/api/nas/storage');
      setStorageInfo(data);
    } catch {
      setStorageInfo(null);
    }
  }

  // ─── API Keys API ───
  const fetchApiKeys = useCallback(async () => {
    setApiKeysLoading(true);
    try {
      const data = await authedFetch('/api/keys');
      setApiKeys(Array.isArray(data) ? data : []);
    } catch {
      setApiKeys([]);
    } finally {
      setApiKeysLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeView === 'settings') {
      fetchStorage();
      fetchApiKeys();
    }
  }, [activeView, fetchApiKeys]);

  async function createApiKey() {
    if (!newKeyName.trim()) return;
    try {
      const data = await authedFetch('/api/keys', {
        method: 'POST',
        body: JSON.stringify({ name: newKeyName }),
      });
      setCreatedKey(data.raw_key);
      setNewKeyName('');
      fetchApiKeys();
    } catch (err) {
      alert('Failed to create API key: ' + err.message);
    }
  }

  async function revokeApiKey(id) {
    if (!window.confirm('Revoke this API key? This cannot be undone.')) return;
    try {
      await authedFetch(`/api/keys/${id}`, { method: 'DELETE' });
      fetchApiKeys();
    } catch (err) {
      alert('Revoke failed: ' + err.message);
    }
  }

  // ─── Multi-select logic ───
  function toggleSelect(item, index, e) {
    const displayList = searchResults || items;
    setSelectedItems(prev => {
      const next = new Set(prev);
      if (e.shiftKey && lastClickedIndex !== null) {
        const start = Math.min(lastClickedIndex, index);
        const end = Math.max(lastClickedIndex, index);
        for (let i = start; i <= end; i++) {
          next.add(displayList[i].path);
        }
      } else if (e.metaKey || e.ctrlKey) {
        if (next.has(item.path)) next.delete(item.path); else next.add(item.path);
      } else {
        if (next.has(item.path) && next.size === 1) next.delete(item.path); else {
          next.clear();
          next.add(item.path);
        }
      }
      return next;
    });
    setLastClickedIndex(index);
  }

  function selectAll() {
    const displayList = searchResults || items;
    if (selectedItems.size === displayList.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(displayList.map(it => it.path)));
    }
  }

  function clearSelection() {
    setSelectedItems(new Set());
    setLastClickedIndex(null);
  }

  // ─── Move dialog logic ───
  function openMoveDialog(paths) {
    setMoveItemPaths(paths);
    setMovePath('');
    setShowMoveDialog(true);
    fetchMoveFolders('');
  }

  async function fetchMoveFolders(dirPath) {
    try {
      const params = new URLSearchParams({ path: dirPath || '' });
      const data = await authedFetch(`/api/nas/list?${params}`);
      setMoveFolders((data.items || []).filter(i => i.type === 'directory'));
      setMovePath(dirPath);
    } catch {
      setMoveFolders([]);
    }
  }

  async function executeMove() {
    setMoveLoading(true);
    try {
      for (const itemPath of moveItemPaths) {
        await authedFetch('/api/nas/move', {
          method: 'POST',
          body: JSON.stringify({ path: itemPath, destination: movePath || '' }),
        });
      }
      setShowMoveDialog(false);
      clearSelection();
      fetchListing(currentPath);
    } catch (err) {
      alert('Move failed: ' + err.message);
    } finally {
      setMoveLoading(false);
    }
  }

  // ─── Bulk operations ───
  async function handleBulkDelete() {
    const count = selectedItems.size;
    if (!window.confirm(`Move ${count} item${count > 1 ? 's' : ''} to trash?`)) return;
    try {
      for (const itemPath of selectedItems) {
        await authedFetch('/api/nas/delete', {
          method: 'DELETE',
          body: JSON.stringify({ path: itemPath }),
        });
      }
      clearSelection();
      fetchListing(currentPath);
    } catch (err) {
      alert('Delete failed: ' + err.message);
    }
  }

  // Search
  async function handleSearch(e) {
    e.preventDefault();
    if (!searchQuery.trim()) { setSearchResults(null); return; }
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ q: searchQuery, dataset: currentPath.split('/')[0] || '' });
      const data = await authedFetch(`/api/nas/search?${params}`);
      setSearchResults(data.results || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  function clearSearch() {
    setSearchQuery('');
    setSearchResults(null);
  }

  function navigateTo(itemPath) {
    setSelectedFile(null);
    clearSearch();
    clearSelection();
    setCurrentPath(itemPath);
  }

  function goBack() {
    const parts = currentPath.split('/').filter(Boolean);
    if (parts.length <= 1) setCurrentPath('');
    else { parts.pop(); setCurrentPath(parts.join('/')); }
    setSelectedFile(null);
    clearSearch();
    clearSelection();
  }

  function getBreadcrumbs() {
    if (!currentPath) return [];
    return currentPath.split('/').filter(Boolean);
  }

  function handleNavigate(view) {
    setActiveView(view);
    setSelectedFile(null);
    clearSearch();
    clearSelection();
  }

  // ─── Upload (tus for large files, multer for small) ───
  // `batch` comes from lib/dropEntries: { files: [{ file, relativeDir }], dirs, failed }.
  // relativeDir is the folder path inside what was dropped ('' for loose files),
  // so a dropped folder tree lands under targetPath with its structure intact.
  async function handleUploadBatch(batch, targetPath) {
    const { files = [], dirs = [], failed = [] } = batch || {};
    if (!files.length && !dirs.length && !failed.length) return;
    const uploadPath = targetPath !== undefined ? targetPath : currentPath;
    const { supabase, getFreshSession } = await import('../lib/supabase');
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;

    const nextId = (name) => `${Date.now()}_${uploadSeq.current++}_${name}`;
    const patchRow = (id, patch) => setUploads(prev => prev.map(u => u.id === id ? { ...u, ...patch } : u));

    // Only refresh the listing if the user is still viewing the folder the
    // file landed in. Reads currentPath from the ref at callback time, not
    // from the closure captured when the upload started. Throttled so a
    // folder of hundreds of small files doesn't fire hundreds of list calls.
    let lastRefresh = 0;
    const refreshIfViewing = (force) => {
      if (currentPathRef.current !== uploadPath) return;
      const now = Date.now();
      if (!force && now - lastRefresh < 2000) return;
      lastRefresh = now;
      fetchListing(uploadPath);
    };

    // Items the browser could not read (an unreadable file inside a dropped
    // folder, for instance) are surfaced as errors rather than skipped silently.
    if (failed.length) {
      setUploads(prev => [...prev, ...failed.map(f => ({
        id: nextId(f.name), name: f.name, progress: 0, status: 'error', error: f.error || 'Could not read file',
      }))]);
    }

    // Empty folders have no file to carry them to the server, so create them explicitly.
    for (const dir of emptyDirsToCreate(files, dirs)) {
      try {
        await authedFetch('/api/nas/mkdir', { method: 'POST', body: JSON.stringify({ path: joinPath(uploadPath, dir) }) });
      } catch (err) {
        setUploads(prev => [...prev, { id: nextId(dir), name: `${dir}/`, progress: 0, status: 'error', error: err.message }]);
      }
    }

    const rows = files.map(({ file, relativeDir }) => ({
      id: nextId(file.name),
      file,
      destPath: joinPath(uploadPath, relativeDir),
      label: relativeDir ? `${relativeDir}/${file.name}` : file.name,
    }));
    setUploads(prev => [...prev, ...rows.map(r => ({ id: r.id, name: r.label, progress: 0, status: 'queued' }))]);

    const uploadLarge = ({ id, file, destPath }) => new Promise((resolve) => {
      const upload = new tus.Upload(file, {
        endpoint: `${API_URL}/api/nas/tus`,
        retryDelays: [0, 1000, 3000, 5000],
        chunkSize: 5 * 1024 * 1024,
        metadata: {
          filename: file.name,
          targetPath: destPath,
          filetype: file.type,
        },
        onBeforeRequest(req) {
          // Refresh the token before every request so a long upload (files
          // up to 10GB) doesn't fail with 401 when the ~1h access token
          // expires mid-transfer. A one-time static header could not resume.
          return getFreshSession().then((s) => {
            if (s) req.setHeader('Authorization', `Bearer ${s.access_token}`);
          });
        },
        onProgress(bytesUploaded, bytesTotal) {
          patchRow(id, { progress: Math.round((bytesUploaded / bytesTotal) * 100) });
        },
        onSuccess() {
          activeTusUploads.current.delete(id);
          patchRow(id, { progress: 100, status: 'done' });
          refreshIfViewing();
          setTimeout(() => setUploads(prev => prev.filter(u => u.id !== id)), 3000);
          resolve();
        },
        onError(err) {
          activeTusUploads.current.delete(id);
          patchRow(id, { status: 'error', error: err.message });
          resolve();
        },
      });
      activeTusUploads.current.set(id, upload);
      patchRow(id, { status: 'uploading' });
      upload.start();
    });

    const uploadSmall = async ({ id, file, destPath }) => {
      patchRow(id, { status: 'uploading' });
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('path', destPath);
        await authedFetch('/api/nas/upload', { method: 'POST', body: formData });
        patchRow(id, { progress: 100, status: 'done' });
        refreshIfViewing();
      } catch (err) {
        patchRow(id, { status: 'error', error: err.message });
      }
    };

    await runWithConcurrency(
      rows.map(row => () => (row.file.size > TUS_THRESHOLD ? uploadLarge(row) : uploadSmall(row))),
      UPLOAD_CONCURRENCY,
    );

    refreshIfViewing(true);
    setTimeout(() => setUploads(prev => prev.filter(u => u.status !== 'done')), 3000);
  }

  // FileList from <input type="file"> (plain picker or webkitdirectory picker).
  // fromFileList snapshots the list immediately — the caller clears the input
  // (e.target.value = '') right after, which empties a live FileList.
  function handleUploadFiles(files, targetPath) {
    return handleUploadBatch(fromFileList(files), targetPath);
  }

  // Drops may contain whole folders. collectFromDataTransfer has to run
  // synchronously inside the handler because the browser empties the
  // DataTransferItemList as soon as the drop event finishes dispatching.
  // The target folder is captured now too, so navigating away while a big
  // folder is still being walked doesn't redirect the upload.
  function handleDrop(e) {
    e.preventDefault();
    setDragOver(false);
    setDragOverFolder(null);
    const target = currentPath;
    collectFromDataTransfer(e.dataTransfer).then(batch => handleUploadBatch(batch, target));
  }

  function handleDragOver(e) { e.preventDefault(); setDragOver(true); }
  function handleDragLeave() { setDragOver(false); }

  function handleFolderDrop(e, folderPath) {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    setDragOverFolder(null);
    collectFromDataTransfer(e.dataTransfer).then(batch => handleUploadBatch(batch, folderPath));
  }

  function handleFolderDragOver(e, folderPath) {
    e.preventDefault();
    e.stopPropagation();
    setDragOverFolder(folderPath);
  }

  function handleFolderDragLeave(e) {
    e.stopPropagation();
    setDragOverFolder(null);
  }

  // ─── Context menu actions ───
  function handleContextMenu(e, item) {
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY, item });
  }

  useEffect(() => {
    function close() { setContextMenu(null); }
    window.addEventListener('click', close);
    return () => window.removeEventListener('click', close);
  }, []);

  async function handleDelete(item) {
    if (!window.confirm(`Move "${item.name}" to trash?`)) return;
    try {
      await authedFetch('/api/nas/delete', {
        method: 'DELETE',
        body: JSON.stringify({ path: item.path }),
      });
      fetchListing(currentPath);
    } catch (err) {
      alert('Delete failed: ' + err.message);
    }
  }

  function startRename(item) {
    setRenaming(item);
    setRenameValue(item.name);
  }

  async function submitRename() {
    if (!renaming || !renameValue.trim() || renameValue === renaming.name) {
      setRenaming(null);
      return;
    }
    try {
      await authedFetch('/api/nas/rename', {
        method: 'POST',
        body: JSON.stringify({ path: renaming.path, newName: renameValue }),
      });
      setRenaming(null);
      fetchListing(currentPath);
    } catch (err) {
      alert('Rename failed: ' + err.message);
    }
  }

  async function handleNewFolder() {
    if (!newFolderName.trim()) { setCreatingFolder(false); return; }
    try {
      const folderPath = currentPath ? `${currentPath}/${newFolderName}` : newFolderName;
      await authedFetch('/api/nas/mkdir', {
        method: 'POST',
        body: JSON.stringify({ path: folderPath }),
      });
      setCreatingFolder(false);
      setNewFolderName('');
      fetchListing(currentPath);
    } catch (err) {
      alert('Create folder failed: ' + err.message);
    }
  }

  async function handleDownload(filePath) {
    // authedUrl refreshes a near-expiry token; the old raw getSession() path
    // could hand window.open a stale JWT after the tab sat idle, 401ing.
    try {
      const url = await authedUrl(`/api/nas/download?path=${encodeURIComponent(filePath)}`);
      window.open(url, '_blank');
    } catch {
      // Not authenticated / refresh failed — nothing to open.
    }
  }

  // Generate authenticated preview URL when a file is selected
  useEffect(() => {
    if (!selectedFile) { setPreviewUrl(null); return; }
    const ext = (selectedFile.extension || '').toLowerCase();
    const needsPreview = IMAGE_EXTENSIONS.includes(ext) || VIDEO_EXTENSIONS.includes(ext) || AUDIO_EXTENSIONS.includes(ext);
    if (!needsPreview) { setPreviewUrl(null); return; }
    let cancelled = false;
    authedUrl(`/api/nas/stream?path=${encodeURIComponent(selectedFile.path)}`)
      .then((url) => { if (!cancelled) setPreviewUrl(url); })
      .catch(() => { if (!cancelled) setPreviewUrl(null); });
    return () => { cancelled = true; };
  }, [selectedFile]);

  // ─── File Detail View ───
  if (selectedFile) {
    const isImage = IMAGE_EXTENSIONS.includes((selectedFile.extension || '').toLowerCase());
    const isVideo = VIDEO_EXTENSIONS.includes((selectedFile.extension || '').toLowerCase());
    const isAudio = AUDIO_EXTENSIONS.includes((selectedFile.extension || '').toLowerCase());

    return (
      <div style={s.layout}>
        <Sidebar user={user} activeView={activeView} onNavigate={handleNavigate} />
        <div style={s.main}>
          <div style={s.header}>
            <button onClick={() => setSelectedFile(null)} className="mc-ghost" style={s.backBtn}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10 2L4 8l6 6" /></svg>
              Back
            </button>
            <h1 style={s.title}>{selectedFile.name}</h1>
          </div>
          <div style={s.body}>
          <div style={s.detailCard}>
            {isImage && previewUrl && (
              <div style={s.previewArea}>
                <img
                  src={previewUrl}
                  alt={selectedFile.name}
                  style={s.previewImg}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              </div>
            )}
            {isVideo && previewUrl && (
              <div style={s.previewArea}>
                <video
                  src={previewUrl}
                  controls
                  style={{ maxWidth: '100%', maxHeight: '400px', borderRadius: '8px' }}
                />
              </div>
            )}
            {isAudio && previewUrl && (
              <div style={s.previewArea}>
                <audio
                  src={previewUrl}
                  controls
                  style={{ width: '100%' }}
                />
              </div>
            )}
            <div style={s.detailMeta}>
              <div style={s.detailRow}><span style={s.detailLabel}>Name</span><span>{selectedFile.name}</span></div>
              <div style={s.detailRow}><span style={s.detailLabel}>Path</span><span style={{ opacity: 0.6, fontSize: '13px' }}>/{selectedFile.path}</span></div>
              <div style={s.detailRow}><span style={s.detailLabel}>Size</span><span>{formatBytes(selectedFile.size)}</span></div>
              <div style={s.detailRow}><span style={s.detailLabel}>Modified</span><span>{formatDate(selectedFile.modified)}</span></div>
              {selectedFile.extension && (
                <div style={s.detailRow}><span style={s.detailLabel}>Type</span><span>.{selectedFile.extension.toUpperCase()}</span></div>
              )}
            </div>
            <button onClick={() => handleDownload(selectedFile.path)} className="mc-btn" style={s.downloadBtn}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2"><path d="M8 2v9M4 8l4 4 4-4M2 13h12" /></svg>
              Download File
            </button>
          </div>
          </div>
        </div>
      </div>
    );
  }

  // ─── Main Layout ───
  const breadcrumbs = getBreadcrumbs();
  const displayItems = searchResults || items;
  const hasSelection = selectedItems.size > 0;

  return (
    <div style={s.layout}>
      <Sidebar user={user} activeView={activeView} onNavigate={handleNavigate} />
      <div
        style={{ ...s.main, ...(dragOver && activeView === 'files' ? { background: colors.accentSoft } : {}) }}
        onDrop={activeView === 'files' ? handleDrop : undefined}
        onDragOver={activeView === 'files' ? handleDragOver : undefined}
        onDragLeave={activeView === 'files' ? handleDragLeave : undefined}
      >
        {/* Files View */}
        {activeView === 'files' && (
          <>
            {/* Header */}
            <div style={s.header}>
              {currentPath && (
                <button onClick={goBack} className="mc-ghost" style={s.backBtn}>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10 2L4 8l6 6" /></svg>
                </button>
              )}
              <div style={s.breadcrumbs}>
                <button onClick={() => { setCurrentPath(''); clearSearch(); clearSelection(); }} className="mc-ghost" style={{ ...s.breadcrumbBtn, ...(breadcrumbs.length === 0 ? { color: colors.text, fontWeight: 600 } : {}) }}>
                  My Files
                </button>
                {breadcrumbs.map((seg, i) => (
                  <React.Fragment key={i}>
                    <span style={s.breadcrumbSep}>/</span>
                    <button
                      onClick={() => { navigateTo(breadcrumbs.slice(0, i + 1).join('/')); }}
                      className="mc-ghost" style={{ ...s.breadcrumbBtn, ...(i === breadcrumbs.length - 1 ? { color: colors.text, fontWeight: 600 } : {}) }}
                    >
                      {seg}
                    </button>
                  </React.Fragment>
                ))}
              </div>
              <div style={s.headerRight}>
                <div style={s.statusDot}>
                  {/* The brand's red dot means live, so a connected drive glows Brick like the logo. */}
                  <span className={nasStatus === null ? 'mc-pulse' : undefined} style={{ ...s.dot, background: nasStatus === null ? statusColors.checking : nasStatus ? statusColors.live : statusColors.offline }} />
                  <span style={s.statusText}>
                    {nasStatus === null ? 'Checking…' : nasStatus ? 'Live' : 'Offline'}
                  </span>
                </div>
              </div>
            </div>

            <div style={s.body}>
            {/* Toolbar — selection or normal */}
            {hasSelection ? (
              <div style={s.selectionToolbar}>
                <span style={s.selectionCount}>{selectedItems.size} selected</span>
                <button onClick={clearSelection} className="mc-ghost" style={s.smallBtn}>Clear</button>
                <button onClick={() => openMoveDialog(Array.from(selectedItems))} className="mc-ghost" style={s.smallBtn}>Move to...</button>
                <button onClick={handleBulkDelete} className="mc-ghost" style={s.smallBtnDanger}>Delete</button>
              </div>
            ) : (
              <div style={s.toolbar}>
                <form onSubmit={handleSearch} style={s.searchForm}>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke={colors.textFaint} strokeWidth="2" style={{ flexShrink: 0 }}>
                    <circle cx="7" cy="7" r="5" /><path d="M11 11l3 3" />
                  </svg>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    placeholder="Search files..."
                    style={s.searchInput}
                  />
                  {searchResults && <button type="button" onClick={clearSearch} className="mc-ghost" style={s.clearSearchBtn}>Clear</button>}
                </form>
                <button onClick={() => setCreatingFolder(true)} className="mc-ghost" style={s.actionBtn} title="New folder">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M3 3h4l1.5 1.5H13a1 1 0 011 1V12a1 1 0 01-1 1H3a1 1 0 01-1-1V4a1 1 0 011-1z" /><path d="M8 7v4M6 9h4" /></svg>
                </button>
                <button onClick={() => folderInputRef.current?.click()} className="mc-ghost" style={s.actionBtn} title="Upload folder">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M3 3h4l1.5 1.5H13a1 1 0 011 1V12a1 1 0 01-1 1H3a1 1 0 01-1-1V4a1 1 0 011-1z" /><path d="M8 11.5V7.5M6.25 9.25L8 7.5l1.75 1.75" /></svg>
                </button>
                <input
                  ref={folderInputRef}
                  type="file"
                  multiple
                  webkitdirectory=""
                  style={{ display: 'none' }}
                  onChange={e => { if (e.target.files.length) handleUploadFiles(e.target.files); e.target.value = ''; }}
                />
                <button onClick={() => fileInputRef.current?.click()} className="mc-btn" style={s.uploadBtn}>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2"><path d="M8 12V3M4 7l4-4 4 4" /><path d="M2 13h12" /></svg>
                  Upload
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  style={{ display: 'none' }}
                  onChange={e => { if (e.target.files.length) handleUploadFiles(e.target.files); e.target.value = ''; }}
                />
                <div style={s.viewControls}>
                  <select value={sortBy} onChange={e => setSortBy(e.target.value)} style={s.sortSelect}>
                    <option value="name">Name</option>
                    <option value="size">Size</option>
                    <option value="modified">Modified</option>
                  </select>
                  <button onClick={() => setSortOrder(o => o === 'asc' ? 'desc' : 'asc')} className="mc-ghost" style={s.iconBtn}>
                    {sortOrder === 'asc' ? '\u2191' : '\u2193'}
                  </button>
                  <button onClick={() => setViewMode(v => v === 'grid' ? 'list' : 'grid')} className="mc-ghost" style={s.iconBtn}>
                    {viewMode === 'grid' ? (
                      <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor"><rect x="1" y="1" width="6" height="6" rx="1" /><rect x="9" y="1" width="6" height="6" rx="1" /><rect x="1" y="9" width="6" height="6" rx="1" /><rect x="9" y="9" width="6" height="6" rx="1" /></svg>
                    ) : (
                      <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor"><rect x="1" y="2" width="14" height="3" rx="1" /><rect x="1" y="7" width="14" height="3" rx="1" /><rect x="1" y="12" width="14" height="3" rx="1" /></svg>
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* New folder input */}
            {creatingFolder && (
              <div style={s.newFolderRow}>
                <FileIcon type="folder" />
                <input
                  autoFocus
                  value={newFolderName}
                  onChange={e => setNewFolderName(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleNewFolder(); if (e.key === 'Escape') { setCreatingFolder(false); setNewFolderName(''); } }}
                  onBlur={handleNewFolder}
                  placeholder="Folder name..."
                  style={s.renameInput}
                />
              </div>
            )}

            {/* Upload progress */}
            {uploads.length > 0 && (
              <div style={s.uploadsBar}>
                {uploads.length > 3 && (
                  <div style={s.uploadSummary}>
                    {uploads.filter(u => u.status === 'done').length} of {uploads.length} done
                    {uploads.some(u => u.status === 'error') ? ` · ${uploads.filter(u => u.status === 'error').length} failed` : ''}
                  </div>
                )}
                {uploads.map(u => (
                  <div key={u.id} style={s.uploadItem}>
                    <span style={s.uploadName} title={u.name}>{u.name}</span>
                    <span style={{ ...s.uploadStatus, color: u.status === 'error' ? colors.danger : u.status === 'done' ? colors.success : u.status === 'queued' ? colors.textMuted : colors.syncText }}>
                      {u.status === 'queued' ? 'Waiting...' : u.status === 'uploading' ? `Uploading... ${u.progress}%` : u.status === 'done' ? 'Synced' : u.error || 'Error'}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* Drag overlay */}
            {dragOver && !dragOverFolder && (
              <div style={s.dragOverlay}>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke={colors.accent} strokeWidth="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" /></svg>
                <div style={{ fontSize: '18px', fontWeight: 600, color: colors.accentText }}>Drop files or folders to upload</div>
              </div>
            )}

            {error && <div style={s.errorMsg}>{error}</div>}

            {/* Content */}
            {loading ? (
              <div style={s.emptyMsg}>Loading...</div>
            ) : displayItems.length === 0 ? (
              <div style={s.emptyState}>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke={colors.textFaint} strokeWidth="1.5">
                  <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z" />
                </svg>
                <div style={{ fontSize: '15px', color: colors.textMuted, marginTop: '12px' }}>
                  {searchResults ? 'No results found.' : 'This folder is empty.'}
                </div>
                <div style={{ fontSize: '13px', color: colors.textFaint, marginTop: '4px' }}>
                  Drag files or folders here or click Upload
                </div>
              </div>
            ) : viewMode === 'grid' ? (
              <div style={s.fileGrid}>
                {displayItems.map((item, i) => {
                  const isSelected = selectedItems.has(item.path);
                  return (
                    <div
                      key={item.path}
                      onClick={() => {
                        if (hasSelection) { toggleSelect(item, i, {}); return; }
                        if (item.type === 'directory') navigateTo(item.path); else setSelectedFile(item);
                      }}
                      onContextMenu={(e) => handleContextMenu(e, item)}
                      onDrop={item.type === 'directory' ? (e) => handleFolderDrop(e, item.path) : undefined}
                      onDragOver={item.type === 'directory' ? (e) => handleFolderDragOver(e, item.path) : undefined}
                      onDragLeave={item.type === 'directory' ? handleFolderDragLeave : undefined}
                      className="mc-card" style={{
                        ...s.fileCard,
                        ...(isSelected ? s.fileCardSelected : {}),
                        ...(dragOverFolder === item.path ? s.folderDropTarget : {}),
                        position: 'relative',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => {}}
                        onClick={(e) => { e.stopPropagation(); toggleSelect(item, i, e); }}
                        style={{ ...s.checkbox, position: 'absolute', top: '8px', left: '8px' }}
                      />
                      <button
                        onClick={(e) => { e.stopPropagation(); toggleFavorite(item.path); }}
                        style={{ ...s.starBtn, ...(favorites.has(item.path) ? s.starBtnActive : {}), position: 'absolute', top: '6px', right: '6px' }}
                      >
                        {favorites.has(item.path) ? '\u2605' : '\u2606'}
                      </button>
                      {hasThumbnail(item) ? (
                        <Thumbnail filePath={item.path} size={80} />
                      ) : (
                        <FileIcon type={item.type === 'directory' ? 'folder' : getFileIcon(item.extension)} />
                      )}
                      {renaming?.path === item.path ? (
                        <input
                          autoFocus
                          value={renameValue}
                          onChange={e => setRenameValue(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') submitRename(); if (e.key === 'Escape') setRenaming(null); }}
                          onBlur={submitRename}
                          onClick={e => e.stopPropagation()}
                          style={s.renameInput}
                        />
                      ) : (
                        <div style={s.fileName}>{item.name}</div>
                      )}
                      <div style={s.fileMeta}>
                        {item.type === 'directory' ? 'Folder' : formatBytes(item.size)}
                        {item.modified ? ` · ${formatDate(item.modified)}` : ''}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={s.fileList}>
                <div style={s.listHeader}>
                  <input
                    type="checkbox"
                    checked={displayItems.length > 0 && selectedItems.size === displayItems.length}
                    onChange={selectAll}
                    style={s.checkbox}
                  />
                  <span style={{ width: '28px' }} />
                  <span style={{ ...s.listHeaderCell, flex: 1 }}>Name</span>
                  <span style={{ ...s.listHeaderCell, width: '32px' }} />
                  <span style={{ ...s.listHeaderCell, width: '80px', textAlign: 'right' }}>Size</span>
                  <span style={{ ...s.listHeaderCell, width: '120px', textAlign: 'right' }}>Modified</span>
                </div>
                {displayItems.map((item, i) => {
                  const isSelected = selectedItems.has(item.path);
                  return (
                    <div
                      key={item.path}
                      onClick={() => {
                        if (hasSelection) { toggleSelect(item, i, {}); return; }
                        if (item.type === 'directory') navigateTo(item.path); else setSelectedFile(item);
                      }}
                      onContextMenu={(e) => handleContextMenu(e, item)}
                      onDrop={item.type === 'directory' ? (e) => handleFolderDrop(e, item.path) : undefined}
                      onDragOver={item.type === 'directory' ? (e) => handleFolderDragOver(e, item.path) : undefined}
                      onDragLeave={item.type === 'directory' ? handleFolderDragLeave : undefined}
                      className="mc-row" style={{
                        ...s.fileListRow,
                        ...(isSelected ? s.fileListRowSelected : {}),
                        ...(dragOverFolder === item.path ? s.folderDropTarget : {}),
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => {}}
                        onClick={(e) => { e.stopPropagation(); toggleSelect(item, i, e); }}
                        style={s.checkbox}
                      />
                      {hasThumbnail(item) ? (
                        <Thumbnail filePath={item.path} size={20} />
                      ) : (
                        <FileIcon type={item.type === 'directory' ? 'folder' : getFileIcon(item.extension)} />
                      )}
                      {renaming?.path === item.path ? (
                        <input
                          autoFocus
                          value={renameValue}
                          onChange={e => setRenameValue(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') submitRename(); if (e.key === 'Escape') setRenaming(null); }}
                          onBlur={submitRename}
                          onClick={e => e.stopPropagation()}
                          style={{ ...s.renameInput, flex: 1 }}
                        />
                      ) : (
                        <span style={s.fileListName}>{item.name}</span>
                      )}
                      <button
                        onClick={(e) => { e.stopPropagation(); toggleFavorite(item.path); }}
                        style={{ ...s.starBtn, ...(favorites.has(item.path) ? s.starBtnActive : {}) }}
                      >
                        {favorites.has(item.path) ? '\u2605' : '\u2606'}
                      </button>
                      <span style={s.fileListSize}>{item.type === 'directory' ? '—' : formatBytes(item.size)}</span>
                      <span style={s.fileListDate}>{formatDate(item.modified)}</span>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Context menu */}
            {contextMenu && (
              <div style={{ ...s.contextMenu, left: contextMenu.x, top: contextMenu.y }}>
                {contextMenu.item.type === 'file' && (
                  <button className="mc-menu-item" style={s.contextMenuItem} onClick={() => handleDownload(contextMenu.item.path)}>Download</button>
                )}
                <button className="mc-menu-item" style={s.contextMenuItem} onClick={() => openCreateShare(contextMenu.item.path, contextMenu.item.type)}>Share</button>
                <button className="mc-menu-item" style={s.contextMenuItem} onClick={() => startRename(contextMenu.item)}>Rename</button>
                <button className="mc-menu-item" style={s.contextMenuItem} onClick={() => openMoveDialog([contextMenu.item.path])}>Move to...</button>
                <button className="mc-menu-item" style={s.contextMenuItem} onClick={() => toggleFavorite(contextMenu.item.path)}>
                  {favorites.has(contextMenu.item.path) ? 'Unfavorite' : 'Favorite'}
                </button>
                {userRole === 'admin' && contextMenu.item.type === 'directory' && (
                  <button className="mc-menu-item" style={s.contextMenuItem} onClick={() => openAccessDialog(contextMenu.item)}>Manage Access</button>
                )}
                <button className="mc-menu-item" style={{ ...s.contextMenuItem, ...s.contextMenuItemDanger }} onClick={() => handleDelete(contextMenu.item)}>Delete</button>
              </div>
            )}
            </div>
          </>
        )}

        {/* Favorites View */}
        {activeView === 'favorites' && (
          <FavoritesView
            items={favoriteItems}
            loading={favoritesLoading}
            onToggleFavorite={toggleFavorite}
            onNavigateToFile={(filePath) => {
              const parts = filePath.split('/');
              parts.pop();
              setActiveView('files');
              setCurrentPath(parts.join('/'));
            }}
          />
        )}

        {/* Shared Links View */}
        {activeView === 'shared' && (
          <SharedLinksView
            links={shareLinks}
            loading={shareLinksLoading}
            onRevoke={revokeShareLink}
            onCopy={copyShareUrl}
            copiedLink={copiedLink}
          />
        )}

        {/* Trash View */}
        {activeView === 'trash' && (
          <TrashView
            items={trashItems}
            loading={trashLoading}
            onRestore={restoreTrashItem}
            onDelete={permanentDeleteTrashItem}
            onEmpty={emptyTrash}
          />
        )}

        {/* Settings View */}
        {activeView === 'settings' && (
          <SettingsView
            user={user}
            userRole={userRole}
            storageInfo={storageInfo}
            signOut={signOut}
            apiKeys={apiKeys}
            apiKeysLoading={apiKeysLoading}
            showCreateKey={showCreateKey}
            setShowCreateKey={setShowCreateKey}
            newKeyName={newKeyName}
            setNewKeyName={setNewKeyName}
            createdKey={createdKey}
            setCreatedKey={setCreatedKey}
            onCreateKey={createApiKey}
            onRevokeKey={revokeApiKey}
          />
        )}
      </div>

      {/* Access Management Dialog */}
      {showAccessDialog && (
        <div style={s.dialogOverlay} onClick={() => setShowAccessDialog(null)}>
          <div style={s.dialogCard} onClick={e => e.stopPropagation()}>
            <div style={s.dialogTitle}>Manage Access</div>
            <div style={s.dialogSubtitle}>/{showAccessDialog.path}</div>
            {accessLoading ? (
              <div style={{ color: colors.textSecondary, fontSize: '13px', padding: '12px 0' }}>Loading...</div>
            ) : (
              <>
                <div style={{ fontSize: '13px', color: colors.textMuted, marginBottom: '12px' }}>
                  Unchecking a role hides this folder and its contents from that role.
                </div>
                {[
                  { role: 'admin', label: 'Admin', locked: true },
                  { role: 'member', label: 'Member', locked: false },
                  { role: 'viewer', label: 'Viewer', locked: false },
                ].map(({ role, label, locked }) => (
                  <label key={role} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 0', cursor: locked ? 'default' : 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={locked ? true : !accessBlocked[role]}
                      disabled={locked}
                      onChange={() => {
                        if (!locked) setAccessBlocked(prev => ({ ...prev, [role]: !prev[role] }));
                      }}
                      style={{ width: '16px', height: '16px', accentColor: colors.accent }}
                    />
                    <span style={{ fontSize: '14px', color: locked ? colors.textMuted : colors.text }}>
                      {label} {locked && '(always visible)'}
                    </span>
                  </label>
                ))}
                {allUsers.filter(u => u.role !== 'admin').length > 0 && (
                  <>
                    <div style={{ borderTop: `1px solid ${colors.border}`, margin: '16px 0 12px' }} />
                    <div style={{ fontSize: '13px', color: colors.textMuted, marginBottom: '12px' }}>
                      Block specific users from this folder (admins always have access).
                    </div>
                    {allUsers.filter(u => u.role !== 'admin').map(user => (
                      <label key={user.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '6px 0', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={!blockedUsers.has(user.id)}
                          onChange={() => {
                            setBlockedUsers(prev => {
                              const next = new Set(prev);
                              if (next.has(user.id)) next.delete(user.id);
                              else next.add(user.id);
                              return next;
                            });
                          }}
                          style={{ width: '16px', height: '16px', accentColor: colors.accent }}
                        />
                        <span style={{ fontSize: '14px', color: colors.text }}>
                          {user.display_name || user.email}
                        </span>
                        <span style={{ fontSize: '12px', color: colors.textFaint, marginLeft: 'auto' }}>
                          {user.role}
                        </span>
                      </label>
                    ))}
                  </>
                )}
                <div style={s.dialogActions}>
                  <button onClick={() => setShowAccessDialog(null)} className="mc-ghost" style={s.dialogCancelBtn}>Cancel</button>
                  <button onClick={saveAccessRestrictions} className="mc-btn" style={s.dialogConfirmBtn}>Save</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Create Share Dialog */}
      {showCreateShare && (
        <div style={s.dialogOverlay} onClick={() => { setShowCreateShare(null); setCreatedShareLink(null); }}>
          <div style={s.dialogCard} onClick={e => e.stopPropagation()}>
            <div style={s.dialogTitle}>Create Share Link</div>
            <div style={s.dialogSubtitle}>/{showCreateShare.path}</div>

            {!createdShareLink ? (
              <>
                <div style={s.dialogField}>
                  <label style={s.dialogLabel}>Mode</label>
                  <select value={shareMode} onChange={e => setShareMode(e.target.value)} style={s.dialogSelect}>
                    <option value="upload">Upload only</option>
                    <option value="download">Download only</option>
                    <option value="both">Upload & Download</option>
                  </select>
                </div>
                <div style={s.dialogField}>
                  <label style={s.dialogLabel}>Expires (optional)</label>
                  <input
                    type="datetime-local"
                    value={shareExpiry}
                    onChange={e => setShareExpiry(e.target.value)}
                    style={s.dialogInput}
                  />
                </div>
                <div style={s.dialogField}>
                  <label style={s.dialogLabel}>Max uses (optional)</label>
                  <input
                    type="number"
                    min="1"
                    value={shareMaxUses}
                    onChange={e => setShareMaxUses(e.target.value)}
                    placeholder="Unlimited"
                    style={s.dialogInput}
                  />
                </div>
                <div style={s.dialogActions}>
                  <button onClick={() => { setShowCreateShare(null); setCreatedShareLink(null); }} className="mc-ghost" style={s.dialogCancelBtn}>Cancel</button>
                  <button onClick={createShareLink} className="mc-btn" style={s.dialogConfirmBtn}>Create Link</button>
                </div>
              </>
            ) : (
              <>
                <div style={{ ...s.dialogField, marginTop: '8px' }}>
                  <label style={s.dialogLabel}>Share URL</label>
                  <div style={s.linkCopyRow}>
                    <input
                      readOnly
                      value={createdShareLink}
                      style={s.linkCopyInput}
                      onFocus={e => e.target.select()}
                    />
                    <button onClick={() => copyShareUrl(createdShareLink)} className="mc-btn" style={s.linkCopyBtn}>
                      {copiedLink === createdShareLink ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                </div>
                <div style={s.dialogActions}>
                  <button onClick={() => { setShowCreateShare(null); setCreatedShareLink(null); }} className="mc-btn" style={s.dialogConfirmBtn}>Done</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Move Dialog */}
      {showMoveDialog && (
        <div style={s.dialogOverlay} onClick={() => setShowMoveDialog(false)}>
          <div style={{ ...s.dialogCard, maxWidth: '480px' }} onClick={e => e.stopPropagation()}>
            <div style={s.dialogTitle}>Move {moveItemPaths.length} item{moveItemPaths.length > 1 ? 's' : ''}</div>
            <div style={s.dialogSubtitle}>Select destination folder</div>

            {/* Move breadcrumbs */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '12px', flexWrap: 'wrap' }}>
              <button
                onClick={() => fetchMoveFolders('')}
                className="mc-ghost" style={{ ...s.breadcrumbBtn, ...(movePath === '' ? { color: colors.text, fontWeight: 600 } : {}) }}
              >
                Root
              </button>
              {movePath && movePath.split('/').filter(Boolean).map((seg, i, arr) => (
                <React.Fragment key={i}>
                  <span style={s.breadcrumbSep}>/</span>
                  <button
                    onClick={() => fetchMoveFolders(arr.slice(0, i + 1).join('/'))}
                    className="mc-ghost" style={{ ...s.breadcrumbBtn, ...(i === arr.length - 1 ? { color: colors.text, fontWeight: 600 } : {}) }}
                  >
                    {seg}
                  </button>
                </React.Fragment>
              ))}
            </div>

            {/* Folder list */}
            <div style={{ maxHeight: '240px', overflow: 'auto', marginBottom: '16px', border: `1px solid ${colors.border}`, borderRadius: '8px' }}>
              {moveFolders.length === 0 ? (
                <div style={{ padding: '20px', textAlign: 'center', color: colors.textFaint, fontSize: '13px' }}>No subfolders</div>
              ) : (
                moveFolders.map((folder, i) => (
                  <button
                    key={i}
                    onClick={() => fetchMoveFolders(folder.path)}
                    className="mc-row" style={{ ...s.fileListRow, borderBottom: `1px solid ${colors.border}` }}
                  >
                    <FileIcon type="folder" />
                    <span style={s.fileListName}>{folder.name}</span>
                  </button>
                ))
              )}
            </div>

            <div style={s.dialogActions}>
              <button onClick={() => setShowMoveDialog(false)} className="mc-ghost" style={s.dialogCancelBtn}>Cancel</button>
              <button onClick={executeMove} disabled={moveLoading} className="mc-btn" style={s.dialogConfirmBtn}>
                {moveLoading ? 'Moving...' : 'Move Here'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Favorites View ───
function FavoritesView({ items, loading, onToggleFavorite, onNavigateToFile }) {
  return (
    <>
      <div style={s.header}>
        <h1 style={s.title}>Favorites</h1>
      </div>
      <div style={s.body}>
      {loading ? (
        <div style={s.emptyMsg}>Loading...</div>
      ) : items.length === 0 ? (
        <div style={s.emptyState}>
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke={colors.textFaint} strokeWidth="1.5">
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
          </svg>
          <div style={{ fontSize: '15px', color: colors.textMuted, marginTop: '12px' }}>
            No favorites yet.
          </div>
          <div style={{ fontSize: '13px', color: colors.textFaint, marginTop: '4px' }}>
            Click the star on any file to add it here.
          </div>
        </div>
      ) : (
        <div style={s.fileList}>
          <div style={s.listHeader}>
            <span style={{ width: '28px' }} />
            <span style={{ ...s.listHeaderCell, flex: 1 }}>Name</span>
            <span style={{ ...s.listHeaderCell, width: '80px', textAlign: 'right' }}>Size</span>
            <span style={{ ...s.listHeaderCell, width: '120px', textAlign: 'right' }}>Modified</span>
            <span style={{ ...s.listHeaderCell, width: '80px', textAlign: 'right' }}>Actions</span>
          </div>
          {items.map(item => (
            <div
              key={item.id}
              className="mc-row" style={{ ...s.fileListRow, ...(item.missing ? { opacity: 0.4 } : {}) }}
              onClick={() => { if (!item.missing) onNavigateToFile(item.file_path); }}
            >
              <FileIcon type={item.type === 'directory' ? 'folder' : getFileIcon(item.extension)} />
              <span style={s.fileListName}>{item.name}</span>
              <span style={s.fileListSize}>{item.missing ? 'Missing' : item.type === 'directory' ? '—' : formatBytes(item.size)}</span>
              <span style={s.fileListDate}>{formatDate(item.modified)}</span>
              <span style={{ width: '80px', display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  onClick={(e) => { e.stopPropagation(); onToggleFavorite(item.file_path); }}
                  style={{ ...s.starBtn, ...s.starBtnActive }}
                >
                  {'\u2605'}
                </button>
              </span>
            </div>
          ))}
        </div>
      )}
      </div>
    </>
  );
}

// ─── Shared Links View ───
function SharedLinksView({ links, loading, onRevoke, onCopy, copiedLink }) {
  const WEB = process.env.REACT_APP_WEB_URL || window.location.origin;
  return (
    <>
      <div style={s.header}>
        <h1 style={s.title}>Shared Links</h1>
      </div>
      <div style={s.body}>
      {loading ? (
        <div style={s.emptyMsg}>Loading...</div>
      ) : links.length === 0 ? (
        <div style={s.emptyState}>
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke={colors.textFaint} strokeWidth="1.5">
            <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" />
            <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" />
          </svg>
          <div style={{ fontSize: '15px', color: colors.textMuted, marginTop: '12px' }}>
            No share links yet.
          </div>
          <div style={{ fontSize: '13px', color: colors.textFaint, marginTop: '4px' }}>
            Right-click a file or folder and select "Share" to create one.
          </div>
        </div>
      ) : (
        <div style={s.fileList}>
          <div style={s.listHeader}>
            <span style={{ ...s.listHeaderCell, flex: 1 }}>Path</span>
            <span style={{ ...s.listHeaderCell, width: '80px' }}>Mode</span>
            <span style={{ ...s.listHeaderCell, width: '70px', textAlign: 'right' }}>Uses</span>
            <span style={{ ...s.listHeaderCell, width: '100px', textAlign: 'right' }}>Expires</span>
            <span style={{ ...s.listHeaderCell, width: '120px', textAlign: 'right' }}>Actions</span>
          </div>
          {links.map(link => {
            const url = `${WEB}/drop/${link.token}`;
            return (
              <div key={link.id} className="mc-row" style={s.fileListRow}>
                <span style={s.fileListName}>/{link.target_path}</span>
                <span style={{ width: '80px', fontSize: '12px', color: colors.textSecondary }}>
                  {link.mode}
                </span>
                <span style={{ width: '70px', fontSize: '12px', color: colors.textMuted, textAlign: 'right' }}>
                  {link.used_count}{link.max_uses ? `/${link.max_uses}` : ''}
                </span>
                <span style={s.fileListDate}>
                  {link.expires_at ? formatDate(link.expires_at) : 'Never'}
                </span>
                <span style={{ width: '120px', display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                  <button onClick={() => onCopy(url)} className="mc-ghost" style={s.smallBtn}>
                    {copiedLink === url ? 'Copied!' : 'Copy'}
                  </button>
                  <button onClick={() => onRevoke(link.id)} className="mc-ghost" style={s.smallBtnDanger}>
                    Revoke
                  </button>
                </span>
              </div>
            );
          })}
        </div>
      )}
      </div>
    </>
  );
}

// ─── Trash View ───
function TrashView({ items, loading, onRestore, onDelete, onEmpty }) {
  return (
    <>
      <div style={s.header}>
        <h1 style={s.title}>Trash</h1>
        <div style={s.headerRight}>
          {items.length > 0 && (
            <button onClick={onEmpty} className="mc-ghost" style={s.smallBtnDanger}>
              Empty Trash
            </button>
          )}
        </div>
      </div>
      <div style={s.body}>
      {loading ? (
        <div style={s.emptyMsg}>Loading...</div>
      ) : items.length === 0 ? (
        <div style={s.emptyState}>
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke={colors.textFaint} strokeWidth="1.5">
            <polyline points="3 6 5 6 21 6" />
            <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" />
            <path d="M10 11v6M14 11v6M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2" />
          </svg>
          <div style={{ fontSize: '15px', color: colors.textMuted, marginTop: '12px' }}>
            Trash is empty.
          </div>
        </div>
      ) : (
        <div style={s.fileList}>
          <div style={s.listHeader}>
            <span style={{ width: '28px' }} />
            <span style={{ ...s.listHeaderCell, flex: 1 }}>Name</span>
            <span style={{ ...s.listHeaderCell, width: '80px', textAlign: 'right' }}>Size</span>
            <span style={{ ...s.listHeaderCell, width: '120px', textAlign: 'right' }}>Deleted</span>
            <span style={{ ...s.listHeaderCell, width: '130px', textAlign: 'right' }}>Actions</span>
          </div>
          {items.map(item => (
            <div key={item.trashName} className="mc-row" style={s.fileListRow}>
              <FileIcon type={item.type === 'directory' ? 'folder' : getFileIcon(item.extension)} />
              <span style={s.fileListName}>{item.originalName}</span>
              <span style={s.fileListSize}>{item.type === 'directory' ? '—' : formatBytes(item.size)}</span>
              <span style={s.fileListDate}>{formatDate(item.deletedAt)}</span>
              <span style={{ width: '130px', display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                <button onClick={() => onRestore(item.trashName)} className="mc-ghost" style={s.smallBtn}>Restore</button>
                <button onClick={() => onDelete(item.trashName)} className="mc-ghost" style={s.smallBtnDanger}>Delete</button>
              </span>
            </div>
          ))}
        </div>
      )}
      </div>
    </>
  );
}

// ─── Settings View ───
const ROLE_COLORS = { admin: colors.sand, member: colors.accent, viewer: colors.textMuted };

function SettingsView({ user, userRole, storageInfo, signOut, apiKeys, apiKeysLoading, showCreateKey, setShowCreateKey, newKeyName, setNewKeyName, createdKey, setCreatedKey, onCreateKey, onRevokeKey }) {
  const [copiedKey, setCopiedKey] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [roleUpdating, setRoleUpdating] = useState(null);
  const [resetSent, setResetSent] = useState(false);
  const [resetSending, setResetSending] = useState(null);
  const [deleting, setDeleting] = useState(null);
  // Tide while healthy, Sand as a warning, Brick only when nearly full.
  const percentColor = !storageInfo ? colors.accent
    : storageInfo.percent >= 90 ? colors.sync
    : storageInfo.percent >= 70 ? colors.sand
    : colors.accent;

  useEffect(() => {
    if (userRole !== 'admin') return;
    setUsersLoading(true);
    authedFetch('/api/restrictions/admin/users')
      .then(setAllUsers)
      .catch(() => {})
      .finally(() => setUsersLoading(false));
  }, [userRole]);

  async function handleRoleChange(userId, newRole) {
    setRoleUpdating(userId);
    try {
      const updated = await authedFetch(`/api/restrictions/admin/users/${userId}/role`, {
        method: 'PUT',
        body: JSON.stringify({ role: newRole }),
      });
      setAllUsers(prev => prev.map(u => u.id === userId ? { ...u, role: updated.role } : u));
    } catch (err) {
      alert('Failed to update role: ' + err.message);
    } finally {
      setRoleUpdating(null);
    }
  }

  async function handleSelfReset() {
    setResetSent(false);
    try {
      await fetch(`${API_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: user?.email }),
      });
      setResetSent(true);
    } catch {
      alert('Failed to send reset email');
    }
  }

  async function handleAdminReset(userId) {
    setResetSending(userId);
    try {
      await authedFetch(`/api/restrictions/admin/users/${userId}/reset-password`, { method: 'POST' });
      setResetSending('done-' + userId);
      setTimeout(() => setResetSending(null), 2000);
    } catch (err) {
      alert('Failed to send reset email: ' + err.message);
      setResetSending(null);
    }
  }

  async function handleDeleteUser(userId) {
    const target = allUsers.find(u => u.id === userId);
    if (!window.confirm(`Delete ${target?.email || 'this user'}? This cannot be undone.`)) return;
    setDeleting(userId);
    try {
      await authedFetch(`/api/restrictions/admin/users/${userId}`, { method: 'DELETE' });
      setAllUsers(prev => prev.filter(u => u.id !== userId));
    } catch (err) {
      alert('Failed to delete user: ' + err.message);
    } finally {
      setDeleting(null);
    }
  }

  function copyKey(key) {
    navigator.clipboard.writeText(key).then(() => {
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 2000);
    }).catch(() => {});
  }

  return (
    <>
      <div style={s.header}>
        <h1 style={s.title}>Settings</h1>
      </div>
      <div style={s.body}>
      <div style={{ ...s.detailCard, maxWidth: '640px' }}>
        <div style={s.detailMeta}>
          <div style={s.detailRow}><span style={s.detailLabel}>Email</span><span>{user?.email}</span></div>
          <div style={s.detailRow}><span style={s.detailLabel}>Joined</span><span>{user?.created_at ? formatDate(user.created_at) : '—'}</span></div>
          <div style={{ ...s.detailRow, marginTop: '4px' }}>
            <span style={s.detailLabel}>Password</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {resetSent
                ? <span style={{ fontSize: '12px', color: colors.success }}>Reset link sent to your email</span>
                : <button onClick={handleSelfReset} className="mc-ghost" style={s.smallBtn}>Reset Password</button>
              }
            </span>
          </div>
        </div>

        {/* Storage bar */}
        <div style={{ marginBottom: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
            <span style={{ fontSize: '12px', fontWeight: 600, color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Storage</span>
            {storageInfo && (
              <span style={{ fontSize: '12px', color: colors.textMuted }}>
                {formatBytes(storageInfo.used)} of {formatBytes(storageInfo.total)} ({storageInfo.percent}%)
              </span>
            )}
          </div>
          <div style={{ height: '8px', borderRadius: '4px', background: colors.active, overflow: 'hidden' }}>
            <div style={{
              height: '100%',
              borderRadius: '4px',
              background: percentColor,
              width: storageInfo ? `${storageInfo.percent}%` : '0%',
              transition: 'width 0.3s',
            }} />
          </div>
        </div>

        {/* API Keys Section */}
        <div style={{ marginBottom: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <span style={{ fontSize: '12px', fontWeight: 600, color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: '0.5px' }}>API Keys</span>
            <button onClick={() => { setShowCreateKey(true); setCreatedKey(null); }} className="mc-ghost" style={s.smallBtn}>Create Key</button>
          </div>

          {/* Create key form */}
          {showCreateKey && (
            <div style={{ marginBottom: '12px', padding: '12px', borderRadius: '8px', background: colors.raised, border: `1px solid ${colors.border}` }}>
              {!createdKey ? (
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    autoFocus
                    value={newKeyName}
                    onChange={e => setNewKeyName(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') onCreateKey(); if (e.key === 'Escape') setShowCreateKey(false); }}
                    placeholder="Key name (e.g. CI/CD)"
                    style={{ ...s.dialogInput, flex: 1 }}
                  />
                  <button onClick={onCreateKey} className="mc-btn" style={s.dialogConfirmBtn}>Create</button>
                  <button onClick={() => setShowCreateKey(false)} className="mc-ghost" style={s.dialogCancelBtn}>Cancel</button>
                </div>
              ) : (
                <div>
                  <div style={{ fontSize: '12px', color: colors.sand, marginBottom: '8px', fontWeight: 500 }}>
                    Copy this key now — you won't be able to see it again.
                  </div>
                  <div style={s.linkCopyRow}>
                    <input readOnly value={createdKey} style={s.linkCopyInput} onFocus={e => e.target.select()} />
                    <button onClick={() => copyKey(createdKey)} className="mc-btn" style={s.linkCopyBtn}>
                      {copiedKey === createdKey ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                  <div style={{ marginTop: '8px', textAlign: 'right' }}>
                    <button onClick={() => { setShowCreateKey(false); setCreatedKey(null); }} className="mc-ghost" style={s.smallBtn}>Done</button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Keys table */}
          {apiKeysLoading ? (
            <div style={{ fontSize: '13px', color: colors.textFaint, padding: '12px 0' }}>Loading...</div>
          ) : apiKeys.length === 0 ? (
            <div style={{ fontSize: '13px', color: colors.textFaint, padding: '12px 0' }}>No API keys yet.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
              {apiKeys.map(key => {
                const isRevoked = !!key.revoked_at;
                return (
                  <div key={key.id} className="mc-row" style={{ ...s.fileListRow, ...(isRevoked ? { opacity: 0.4 } : {}) }}>
                    <span style={{ fontSize: '12px', color: colors.textMuted, width: '80px', fontFamily: font.mono, flexShrink: 0 }}>{key.key_prefix}...</span>
                    <span style={{ ...s.fileListName, ...(isRevoked ? { textDecoration: 'line-through' } : {}) }}>{key.name}</span>
                    <span style={{ fontSize: '11px', color: colors.textFaint, width: '80px', textAlign: 'right', flexShrink: 0 }}>{formatDate(key.created_at)}</span>
                    <span style={{ fontSize: '11px', color: colors.textFaint, width: '80px', textAlign: 'right', flexShrink: 0 }}>{key.last_used_at ? formatDate(key.last_used_at) : 'Never'}</span>
                    <span style={{ width: '70px', display: 'flex', justifyContent: 'flex-end', flexShrink: 0 }}>
                      {isRevoked ? (
                        <span style={{ fontSize: '11px', color: colors.danger }}>Revoked</span>
                      ) : (
                        <button onClick={() => onRevokeKey(key.id)} className="mc-ghost" style={s.smallBtnDanger}>Revoke</button>
                      )}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* User Management — admin only */}
        {userRole === 'admin' && (
          <div style={{ marginBottom: '24px' }}>
            <div style={{ fontSize: '12px', fontWeight: 600, color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '12px' }}>
              User Management
            </div>
            {usersLoading ? (
              <div style={{ fontSize: '13px', color: colors.textFaint, padding: '12px 0' }}>Loading...</div>
            ) : allUsers.length === 0 ? (
              <div style={{ fontSize: '13px', color: colors.textFaint, padding: '12px 0' }}>No users found.</div>
            ) : (
              <div style={{ borderRadius: '10px', border: `1px solid ${colors.border}`, overflow: 'hidden' }}>
                {allUsers.map((u, i) => (
                  <div key={u.id} style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '10px 14px',
                    borderBottom: i < allUsers.length - 1 ? `1px solid ${colors.border}` : 'none',
                    background: colors.raised,
                  }}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontSize: '14px', fontWeight: 500, color: colors.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {u.display_name || u.email}
                      </div>
                      {u.display_name && (
                        <div style={{ fontSize: '12px', color: colors.textFaint, marginTop: '1px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {u.email}
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0, marginLeft: '12px' }}>
                      <select
                        value={u.role}
                        onChange={e => handleRoleChange(u.id, e.target.value)}
                        disabled={roleUpdating === u.id}
                        style={{
                          background: colors.inputBg,
                          border: '1px solid',
                          borderColor: ROLE_COLORS[u.role] || colors.textMuted,
                          borderRadius: '8px',
                          padding: '5px 8px',
                          fontSize: '12px',
                          fontWeight: 600,
                          fontFamily: 'inherit',
                          color: ROLE_COLORS[u.role] || colors.text,
                          cursor: 'pointer',
                          outline: 'none',
                          opacity: roleUpdating === u.id ? 0.5 : 1,
                        }}
                      >
                        <option value="admin">Admin</option>
                        <option value="member">Member</option>
                        <option value="viewer">Viewer</option>
                      </select>
                      <button
                        onClick={() => handleAdminReset(u.id)}
                        disabled={resetSending === u.id}
                        className="mc-ghost" style={{ ...s.smallBtn, opacity: resetSending === u.id ? 0.5 : 1 }}
                      >
                        {resetSending === 'done-' + u.id ? 'Sent!' : 'Reset PW'}
                      </button>
                      {u.id !== user?.id && (
                        <button
                          onClick={() => handleDeleteUser(u.id)}
                          disabled={deleting === u.id}
                          className="mc-ghost" style={{ ...s.smallBtnDanger, opacity: deleting === u.id ? 0.5 : 1 }}
                        >
                          {deleting === u.id ? 'Deleting...' : 'Delete'}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <button onClick={signOut} className="mc-ghost" style={{ ...s.dialogCancelBtn, width: '100%', textAlign: 'center' }}>
          Sign Out
        </button>
      </div>
      </div>
    </>
  );
}

// ─── Sidebar ───
const DESKTOP_RELEASE_API = 'https://api.github.com/repos/IamTrevorMay/mayday-cloud/releases/latest';
const DESKTOP_RELEASE_CACHE_KEY = 'fathom.desktopRelease';
const DESKTOP_RELEASE_CACHE_TTL_MS = 60 * 60 * 1000;
const DESKTOP_RELEASE_FALLBACK = {
  version: 'v0.2.0',
  url: 'https://github.com/IamTrevorMay/mayday-cloud/releases/tag/desktop-v0.2.0',
};

function Sidebar({ user, activeView, onNavigate }) {
  const navItems = [
    { key: 'files', label: 'My Files', icon: 'M3 3h7l2 2h6a1 1 0 011 1v10a1 1 0 01-1 1H3a1 1 0 01-1-1V4a1 1 0 011-1z' },
    { key: 'favorites', label: 'Favorites', icon: 'M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14l-5-4.87 6.91-1.01L12 2z' },
    { key: 'shared', label: 'Shared Links', icon: 'M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71' },
    { key: 'trash', label: 'Trash', icon: 'M3 6h18M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6M10 11v6M14 11v6M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2' },
  ];

  const [desktopRelease, setDesktopRelease] = useState(null);

  useEffect(() => {
    let cancelled = false;

    try {
      const cached = JSON.parse(localStorage.getItem(DESKTOP_RELEASE_CACHE_KEY) || 'null');
      if (cached && Date.now() - cached.fetchedAt < DESKTOP_RELEASE_CACHE_TTL_MS) {
        setDesktopRelease(cached.release);
        return;
      }
    } catch {}

    fetch(DESKTOP_RELEASE_API)
      .then(r => (r.ok ? r.json() : null))
      .then(data => {
        if (cancelled) return;
        if (!data) { setDesktopRelease(DESKTOP_RELEASE_FALLBACK); return; }
        const dmg = (data.assets || []).find(a => a.name && a.name.endsWith('.dmg'));
        const release = dmg
          ? { version: data.tag_name, url: dmg.browser_download_url }
          : DESKTOP_RELEASE_FALLBACK;
        setDesktopRelease(release);
        try {
          localStorage.setItem(DESKTOP_RELEASE_CACHE_KEY, JSON.stringify({ release, fetchedAt: Date.now() }));
        } catch {}
      })
      .catch(() => { if (!cancelled) setDesktopRelease(DESKTOP_RELEASE_FALLBACK); });

    return () => { cancelled = true; };
  }, []);

  return (
    <div style={s.sidebar}>
      <div style={s.sidebarLogo}>
        <Logo variant="horizontal" size={26} />
      </div>
      <nav style={s.sidebarNav}>
        {navItems.map(item => (
          <button
            key={item.key}
            onClick={() => onNavigate(item.key)}
            className="mc-nav" style={{ ...s.sidebarItem, ...(activeView === item.key ? s.sidebarItemActive : {}) }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d={item.icon} />
            </svg>
            {item.label}
          </button>
        ))}
      </nav>
      {/* Settings pinned above footer */}
      <div style={{ marginTop: 'auto', padding: '0 8px' }}>
        {desktopRelease && (
          <button
            onClick={() => window.open(desktopRelease.url, '_blank', 'noopener')}
            className="mc-nav" style={{ ...s.sidebarItem, marginBottom: '4px', flexWrap: 'wrap' }}
            title={`Download Mayday Cloud ${desktopRelease.version} for Mac`}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            <span>Download for Mac</span>
            <span style={{ fontSize: '11px', color: colors.textMuted, marginLeft: 'auto' }}>{desktopRelease.version}</span>
          </button>
        )}
        <button
          onClick={() => onNavigate('settings')}
          className="mc-nav" style={{ ...s.sidebarItem, ...(activeView === 'settings' ? s.sidebarItemActive : {}), marginBottom: '8px' }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="3" />
            <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" />
          </svg>
          Settings
        </button>
      </div>
      <div style={s.sidebarFooter}>
        <div style={s.userInfo}>
          <div style={s.userAvatar}>{(user?.email || '?')[0].toUpperCase()}</div>
          <span style={s.userEmail}>{user?.email}</span>
        </div>
      </div>
    </div>
  );
}

// ─── FileIcon ───
function FileIcon({ type }) {
  // Tide and Sand tag file types per the brand kit; neutrals for the rest.
  const iconColors = {
    folder: colors.sand, video: colors.accent, audio: colors.accentText, image: colors.textSecondary,
    project: colors.sand, doc: colors.textSecondary, archive: colors.textMuted, file: colors.textMuted,
  };
  const paths = {
    folder: 'M4 4h5l2 2h5a1 1 0 011 1v8a1 1 0 01-1 1H4a1 1 0 01-1-1V5a1 1 0 011-1z',
    video: 'M4 4h10a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V6a2 2 0 012-2zM8 8l4 2.5L8 13V8z',
    audio: 'M9 2v14M5 6v8M1 9v2M13 6v8M17 9v2',
    image: 'M2 4h14a2 2 0 012 2v8a2 2 0 01-2 2H2V6a2 2 0 012-2zM5 10a1.5 1.5 0 100-3 1.5 1.5 0 000 3z',
    doc: 'M4 2h8l4 4v10a2 2 0 01-2 2H4a2 2 0 01-2-2V4a2 2 0 012-2zM12 2v4h4M6 9h6M6 12h4',
    archive: 'M4 2h10a2 2 0 012 2v12a2 2 0 01-2 2H4a2 2 0 01-2-2V4a2 2 0 012-2zM8 6v4M6 8h4',
    file: 'M4 2h8l4 4v10a2 2 0 01-2 2H4a2 2 0 01-2-2V4a2 2 0 012-2zM12 2v4h4',
    project: 'M4 4h5l2 2h5a1 1 0 011 1v8a1 1 0 01-1 1H4a1 1 0 01-1-1V5a1 1 0 011-1z',
  };
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke={iconColors[type] || iconColors.file} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
      <path d={paths[type] || paths.file} />
    </svg>
  );
}

// ─── Styles ───
// Composed from src/theme.js. Linear-style shell: flat sidebar on the app
// canvas, the content in an inset bordered panel with a fixed 46px header and
// a scrolling body. Hoverable elements leave `background` out so the mc-*
// classes in index.css can paint it.
const s = {
  layout: {
    display: 'flex',
    height: '100vh',
    background: colors.bg,
    overflow: 'hidden',
  },
  // Sidebar
  sidebar: {
    width: '232px',
    display: 'flex',
    flexDirection: 'column',
    padding: '10px 8px 8px',
    flexShrink: 0,
    minHeight: 0,
  },
  sidebarLogo: {
    display: 'flex',
    alignItems: 'center',
    height: '36px',
    padding: '0 8px',
    marginBottom: '10px',
  },
  sidebarNav: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1px',
  },
  sidebarItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '9px',
    height: '30px',
    padding: '0 8px',
    border: 'none',
    borderRadius: radius.md,
    color: colors.textSecondary,
    fontSize: type.md,
    fontWeight: 500,
    letterSpacing: '-0.01em',
    cursor: 'pointer',
    fontFamily: 'inherit',
    textAlign: 'left',
    width: '100%',
    transition,
  },
  sidebarItemActive: {
    background: colors.active,
    color: colors.text,
  },
  sidebarFooter: {
    padding: '10px 8px 4px',
    marginTop: '8px',
    borderTop: `1px solid ${colors.border}`,
  },
  userInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '9px',
    minWidth: 0,
  },
  userAvatar: {
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    background: colors.accent,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: type.xs,
    fontWeight: 700,
    color: colors.onAccent,
    flexShrink: 0,
  },
  userEmail: {
    fontSize: type.sm,
    color: colors.textMuted,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  // Main panel
  main: {
    flex: 1,
    minWidth: 0,
    margin: '8px 8px 8px 0',
    background: colors.panel,
    border: `1px solid ${colors.border}`,
    borderRadius: radius.lg,
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    overflow: 'hidden',
    transition,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    height: '46px',
    padding: '0 16px',
    borderBottom: `1px solid ${colors.border}`,
    flexShrink: 0,
  },
  headerRight: {
    marginLeft: 'auto',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  body: {
    flex: 1,
    minHeight: 0,
    overflow: 'auto',
    padding: '14px 16px 24px',
  },
  title: {
    ...heading.page,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  backBtn: {
    ...btn.base,
    ...btn.subtle,
    ...btn.sm,
    padding: '0 6px',
    gap: '4px',
    color: colors.textMuted,
  },
  breadcrumbs: {
    display: 'flex',
    alignItems: 'center',
    gap: '2px',
    overflow: 'hidden',
    minWidth: 0,
  },
  breadcrumbBtn: {
    padding: '3px 6px',
    border: 'none',
    borderRadius: radius.sm,
    color: colors.textMuted,
    fontSize: type.md,
    fontWeight: 500,
    cursor: 'pointer',
    fontFamily: 'inherit',
    whiteSpace: 'nowrap',
    transition,
  },
  breadcrumbSep: {
    color: colors.textFaint,
    fontSize: type.md,
    padding: '0 1px',
  },
  statusDot: {
    display: 'flex',
    alignItems: 'center',
    gap: '7px',
  },
  dot: {
    width: '7px',
    height: '7px',
    borderRadius: '50%',
    display: 'inline-block',
  },
  statusText: {
    fontSize: type.sm,
    color: colors.textMuted,
  },
  // Toolbar
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    marginBottom: '12px',
  },
  selectionToolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '12px',
    padding: '5px 6px 5px 12px',
    borderRadius: radius.md,
    background: colors.accentSoft,
    border: `1px solid ${colors.accentBorder}`,
  },
  selectionCount: {
    fontSize: type.md,
    fontWeight: 600,
    color: colors.accentText,
    marginRight: 'auto',
  },
  searchForm: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flex: 1,
    maxWidth: '380px',
    height: '30px',
    padding: '0 10px',
    border: `1px solid ${colors.border}`,
    borderRadius: radius.md,
    background: colors.inputBg,
  },
  searchInput: {
    flex: 1,
    minWidth: 0,
    border: 'none',
    background: 'transparent',
    color: colors.text,
    fontSize: type.md,
    fontFamily: 'inherit',
    outline: 'none',
  },
  clearSearchBtn: {
    ...btn.base,
    ...btn.subtle,
    height: '22px',
    padding: '0 6px',
    fontSize: type.xs,
    color: colors.textMuted,
  },
  actionBtn: {
    ...btn.base,
    ...btn.ghost,
    ...btn.icon,
  },
  uploadBtn: {
    ...btn.base,
    ...btn.sync,
  },
  viewControls: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    marginLeft: 'auto',
  },
  sortSelect: {
    ...selectStyle,
    width: 'auto',
    height: '30px',
    fontSize: type.sm,
    color: colors.textSecondary,
  },
  iconBtn: {
    ...btn.base,
    ...btn.ghost,
    ...btn.icon,
    fontSize: type.md,
  },
  smallBtn: {
    ...btn.base,
    ...btn.ghost,
    ...btn.sm,
  },
  smallBtnDanger: {
    ...btn.base,
    ...btn.danger,
    ...btn.sm,
  },
  // Upload bar
  uploadsBar: {
    ...cardStyle,
    marginBottom: '12px',
    padding: '6px',
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    maxHeight: '260px',
    overflowY: 'auto',
  },
  uploadSummary: {
    ...labelStyle,
    padding: '4px 8px 6px',
  },
  uploadItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '12px',
    padding: '6px 8px',
    borderRadius: radius.sm,
    fontSize: type.md,
  },
  uploadName: {
    color: colors.text,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '70%',
  },
  uploadStatus: {
    fontSize: type.sm,
    fontWeight: 500,
    flexShrink: 0,
    fontVariantNumeric: 'tabular-nums',
  },
  // Folder drop target highlight
  folderDropTarget: {
    background: colors.accentSoft,
    outline: `2px dashed ${colors.accentBorder}`,
    outlineOffset: '-2px',
    borderRadius: radius.md,
  },
  // Drag overlay
  dragOverlay: {
    position: 'absolute',
    inset: 0,
    background: 'rgba(26,23,24,0.92)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    zIndex: 10,
    borderRadius: radius.lg,
    outline: `2px dashed ${colors.accentBorder}`,
    outlineOffset: '-8px',
  },
  // New folder
  newFolderRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '6px 10px',
    marginBottom: '8px',
    borderRadius: radius.md,
    background: colors.raised,
    border: `1px solid ${colors.accentBorder}`,
  },
  renameInput: {
    ...inputStyle,
    width: 'auto',
    height: '26px',
    padding: '0 8px',
    background: colors.overlay,
    borderColor: colors.accentBorder,
  },
  // Checkbox
  checkbox: {
    width: '14px',
    height: '14px',
    accentColor: colors.accent,
    cursor: 'pointer',
    flexShrink: 0,
    margin: 0,
  },
  // Star
  starBtn: {
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    fontSize: '15px',
    color: colors.textFaint,
    padding: '2px 4px',
    lineHeight: 1,
    flexShrink: 0,
    borderRadius: radius.sm,
    transition,
  },
  starBtnActive: {
    color: colors.sand,
  },
  // File grid
  fileGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
    gap: '8px',
  },
  fileCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
    padding: '20px 12px 14px',
    border: `1px solid ${colors.border}`,
    borderRadius: radius.lg,
    background: colors.raised,
    color: colors.text,
    cursor: 'pointer',
    fontFamily: 'inherit',
    textAlign: 'center',
    transition,
  },
  fileCardSelected: {
    border: `1px solid ${colors.accentBorder}`,
    background: colors.accentSoft,
  },
  fileName: {
    fontSize: type.md,
    fontWeight: 500,
    wordBreak: 'break-word',
    lineHeight: 1.3,
  },
  fileMeta: {
    fontSize: type.xs,
    color: colors.textMuted,
  },
  // File list
  fileList: {
    display: 'flex',
    flexDirection: 'column',
  },
  listHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    height: '30px',
    padding: '0 10px',
    ...labelStyle,
    letterSpacing: '0.06em',
    borderBottom: `1px solid ${colors.border}`,
    marginBottom: '2px',
  },
  listHeaderCell: {},
  fileListRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    height: '34px',
    padding: '0 10px',
    border: '1px solid transparent',
    borderRadius: radius.md,
    color: colors.text,
    cursor: 'pointer',
    fontFamily: 'inherit',
    textAlign: 'left',
    width: '100%',
    transition,
  },
  fileListRowSelected: {
    background: colors.accentSoft,
    borderColor: colors.accentBorder,
  },
  fileListName: {
    flex: 1,
    fontSize: type.md,
    fontWeight: 500,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  fileListSize: {
    fontSize: type.sm,
    color: colors.textMuted,
    width: '80px',
    textAlign: 'right',
    fontVariantNumeric: 'tabular-nums',
    flexShrink: 0,
  },
  fileListDate: {
    fontSize: type.sm,
    color: colors.textMuted,
    width: '120px',
    textAlign: 'right',
    fontVariantNumeric: 'tabular-nums',
    flexShrink: 0,
  },
  // Context menu
  contextMenu: {
    position: 'fixed',
    zIndex: 100,
    background: colors.raised,
    border: `1px solid ${colors.borderStrong}`,
    borderRadius: radius.lg,
    padding: '4px',
    minWidth: '160px',
    boxShadow: shadow.menu,
  },
  contextMenuItem: {
    display: 'block',
    width: '100%',
    height: '28px',
    padding: '0 10px',
    border: 'none',
    borderRadius: radius.sm,
    color: colors.text,
    fontSize: type.md,
    cursor: 'pointer',
    fontFamily: 'inherit',
    textAlign: 'left',
    transition,
  },
  contextMenuItemDanger: {
    color: colors.danger,
  },
  // Detail view
  detailCard: {
    ...cardStyle,
    padding: '20px',
  },
  previewArea: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '20px',
    background: colors.bg,
    border: `1px solid ${colors.border}`,
    borderRadius: radius.lg,
    padding: '16px',
  },
  previewImg: {
    maxWidth: '100%',
    maxHeight: '420px',
    borderRadius: radius.md,
    objectFit: 'contain',
  },
  detailMeta: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    marginBottom: '20px',
  },
  detailRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    fontSize: type.md,
    color: colors.text,
  },
  detailLabel: {
    ...labelStyle,
    width: '88px',
    flexShrink: 0,
  },
  downloadBtn: {
    ...btn.base,
    ...btn.primary,
  },
  // Dialog
  dialogOverlay: dialogStyle.overlay,
  dialogCard: dialogStyle.card,
  dialogTitle: dialogStyle.title,
  dialogSubtitle: dialogStyle.subtitle,
  dialogField: {
    marginBottom: '14px',
  },
  dialogLabel: {
    ...labelStyle,
    display: 'block',
    marginBottom: '6px',
  },
  dialogSelect: selectStyle,
  dialogInput: inputStyle,
  dialogActions: dialogStyle.actions,
  dialogCancelBtn: {
    ...btn.base,
    ...btn.ghost,
  },
  dialogConfirmBtn: {
    ...btn.base,
    ...btn.primary,
  },
  linkCopyRow: {
    display: 'flex',
    gap: '8px',
  },
  linkCopyInput: {
    ...inputStyle,
    flex: 1,
    fontSize: type.sm,
    fontFamily: font.mono,
  },
  linkCopyBtn: {
    ...btn.base,
    ...btn.primary,
    height: '32px',
  },
  // States
  errorMsg: {
    padding: '10px 12px',
    borderRadius: radius.md,
    background: colors.dangerSoft,
    border: `1px solid ${colors.dangerBorder}`,
    color: colors.danger,
    fontSize: type.md,
    marginBottom: '12px',
  },
  emptyMsg: {
    color: colors.textMuted,
    fontSize: type.md,
    textAlign: 'center',
    padding: '48px 0',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '72px 0',
    textAlign: 'center',
  },
};
